import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Container, Typography, Grid, Card, Button,
  Stack, Chip, alpha, useTheme, TextField, InputAdornment,
  IconButton, Skeleton, Fade, Zoom, CardContent, Dialog
} from "@mui/material";
import {
  Search, Groups, ArrowBack, LocalMovies, Tv,
  FlashOn, RestartAlt
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getRoomsForBooking } from "../redux/slices/room/thunk";
import LoadingPage from "../components/LoadingComponent";
import BookingContactModal from "../components/BookingContactModal";

const BASE_URL = "https://red-90.com";
const joinUrl = (path) => path?.startsWith("http") ? path : `${BASE_URL}${path}`;

export default function Rooms() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const { roomsForBooking, loading: roomsLoading } = useSelector((state) => state.room);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const primaryMain = theme.palette.primary.main;

  useEffect(() => {
    setLoading(true);
    dispatch(getRoomsForBooking()).finally(() => setLoading(false));
  }, [dispatch]);

  // منطق الفلترة والبحث
  const filteredRooms = useMemo(() => {
    return roomsForBooking.filter((room) => {
      if (room?.isAvailable !== true) return false;
      const title = isRtl ? room.title_ar : room.title_en;
      const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = activeFilter === "all" || room.type === activeFilter;
      return matchesSearch && matchesFilter;
    });
  }, [roomsForBooking, searchQuery, activeFilter, isRtl]);

  if (roomsLoading) return <LoadingPage />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 10 }}>

      {/* 1. Header Section */}
      <Box sx={{ pt: 6, pb: 4, position: 'relative' }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <IconButton
              onClick={() => navigate("/")}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}
            >
              <ArrowBack sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
            </IconButton>
            <Chip
              icon={<FlashOn sx={{ fontSize: '1rem !important' }} />}
              label={
                filteredRooms.some((r) => !r.isBusy)
                  ? t("rooms.status.available")
                  : t("home.rooms.status_busy")
              }
              sx={{
                fontWeight: 900,
                borderRadius: 2,
                bgcolor: filteredRooms.some((r) => !r.isBusy)
                  ? (theme.palette.primary.confirm || "#16A34A")
                  : "#000",
                color: "#fff",
              }}
            />
          </Stack>

          <Typography variant="h2" sx={{ fontWeight: 1000, mb: 1, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            {t("rooms.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontWeight: 500 }}>
            {t("rooms.subtitle")}
          </Typography>

          {/* 2. Search & Filter Tabs */}
          <Stack spacing={2}>
            <TextField
              fullWidth
              placeholder={t("rooms.search_placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                sx: { borderRadius: 4, height: 60, bgcolor: alpha(theme.palette.divider, 0.05), border: 'none' }
              }}
            />
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', py: 1 }}>
              {['all', 'cinema', 'screen'].map((type) => (
                <Button
                  key={type}
                  variant={activeFilter === type ? "contained" : "text"}
                  onClick={() => setActiveFilter(type)}
                  sx={{
                    borderRadius: 3, px: 4, py: 1, fontWeight: 800, whiteSpace: 'nowrap',
                    bgcolor: activeFilter === type ? primaryMain : 'transparent',
                    color: activeFilter === type ? (activeFilter === type ? '#fff' : 'text.secondary') : 'inherit'
                  }}
                >
                  {t(`rooms.types.${type}`)}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* 3. Rooms Grid / Empty State */}
      <Container maxWidth="lg">
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map(i => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 5 }} />
              </Grid>
            ))}
          </Grid>
        ) : filteredRooms.length > 0 ? (
          <Grid container spacing={3} sx={{ direction: isRtl ? "rtl" : "ltr" }}>
            {filteredRooms.map((room) => (
              <Grid item xs={12} sm={6} md={4} key={room._id}>
                <Fade in timeout={500}>
                  <Card
                    onClick={() => {
                      setSelectedRoom(room);
                      setBookDialogOpen(true);
                    }}
                    sx={{
                      height: "100%", borderRadius: 5, p: 1.2,
                      bgcolor: "background.paper",
                      boxShadow: theme.shadows[2],
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": { transform: "translateY(-8px)", boxShadow: theme.shadows[10] }
                    }}
                  >
                    {/* Image Area */}
                    <Box sx={{ position: "relative", height: 220, borderRadius: 4, overflow: "hidden" }}>
                      <Box component="img" src={joinUrl(room.image)} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <Chip
                        label={room.isBusy ? t("home.rooms.status_busy") : t("home.rooms.status_ready")}
                        sx={{
                          position: "absolute", top: 12, right: 12,
                          bgcolor: room.isBusy ? "#000" : (theme.palette.primary.confirm || "#16A34A"),
                          color: "#fff", fontWeight: 900, borderRadius: 2
                        }}
                      />
                      <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 1.5, background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)" }}>
                        <Chip
                          size="small"
                          icon={room.type === 'cinema' ? <LocalMovies sx={{ color: '#fff !important' }} /> : <Tv sx={{ color: '#fff !important' }} />}
                          label={room.type.toUpperCase()}
                          sx={{ bgcolor: alpha(primaryMain, 0.9), color: "#fff", fontWeight: 800, px: 1 }}
                        />
                      </Box>
                    </Box>

                    {/* Card Content */}
                    <CardContent sx={{ px: 1, pt: 2, textAlign: isRtl ? "right" : "left" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 1000 }}>
                          {isRtl ? room.title_ar : room.title_en}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ bgcolor: alpha(primaryMain, 0.05), px: 1, borderRadius: 1 }}>
                          <Groups fontSize="small" color="primary" />
                          <Typography variant="subtitle2" fontWeight={1000} color="primary">{room.capacity}</Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={1} sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", p: 1.5, borderRadius: 3, bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : '#f8f9fa' }}>
                          <Typography variant="caption" fontWeight={800} color="text.secondary">{t("home.rooms.single")}</Typography>
                          <Typography variant="subtitle2" fontWeight={1000}>{room.price_single_hour} EGP</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", p: 1.5, borderRadius: 3, bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : '#f8f9fa' }}>
                          <Typography variant="caption" fontWeight={800} color="text.secondary">{t("home.rooms.multi")}</Typography>
                          <Typography variant="subtitle2" fontWeight={1000}>{room.price_multi_hour} EGP</Typography>
                        </Box>
                      </Stack>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRoom(room);
                          setBookDialogOpen(true);
                        }}
                        sx={{ borderRadius: 4, py: 1.5, fontWeight: 900, boxShadow: `0 8px 16px ${alpha(primaryMain, 0.2)}` }}
                      >
                        {t("home.rooms.book_now")}
                      </Button>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* 4. Enhanced Empty State */
          <Fade in timeout={800}>
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              py: 12, textAlign: 'center', bgcolor: alpha(theme.palette.divider, 0.02),
              borderRadius: 10, border: `2px dashed ${alpha(theme.palette.divider, 0.1)}`, mt: 2
            }}>
              <Box sx={{ fontSize: 100, mb: 2, filter: 'grayscale(0.5)' }}>🔭</Box>
              <Typography variant="h4" fontWeight={1000} gutterBottom>
                {t("rooms.no_results")}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 450 }}>
                {isRtl
                  ? "لا توجد غرف تطابق اختياراتك حالياً. جرب تغيير كلمة البحث أو نوع الغرفة."
                  : "We couldn't find any rooms matching your criteria. Try adjusting your filters or search."}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RestartAlt />}
                onClick={() => { setSearchQuery(""); setActiveFilter("all"); }}
                sx={{ borderRadius: 4, px: 5, py: 2, fontWeight: 900, borderWidth: 2 }}
              >
                {t("rooms.reset_filters")}
              </Button>
            </Box>
          </Fade>
        )}
      </Container>

      <BookingContactModal
        open={bookDialogOpen}
        onClose={() => setBookDialogOpen(false)}
        bookingType="room"
        room={selectedRoom}
        itemTitle={selectedRoom ? (isRtl ? selectedRoom.title_ar : selectedRoom.title_en) : ""}
      />
    </Box>
  );
}